import { type CSSProperties, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ArrowForward, ArrowRight } from "@mui/icons-material";
import { Divider, MenuList, styled } from "~/common/mui";
import useMediaQuery, { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";
import type { TreeNode } from "~/domains/navigation/types/NavigationTypes";
import { NavigationItem as StyledNavigationItem } from "~/features/categoryNavigation/components/CategoryNavigation.styles";
import { getFontStyle, FontSize } from "~/common/components/materialInput/Fonts";

interface NavigationTreeProps {
  tree: TreeNode;
  visibleNodes: TreeNode[];
  onItemClick?: (node: TreeNode) => void;
  onItemHover?: (node: TreeNode, noChildren?: boolean) => void;
  onItemLeave?: (node: TreeNode) => void;
}

function collectAllNodes(node: TreeNode): TreeNode[] {
  const result: TreeNode[] = [node];
  for (const child of node.childNodes) {
    result.push(...collectAllNodes(child));
  }
  return result;
}

export const NavigationTree = ({ tree, visibleNodes, onItemClick, onItemHover, onItemLeave }: NavigationTreeProps) => {
  const { t } = useTranslation();

  const allNodes = useMemo(() => collectAllNodes(tree), [tree]);

  return (
    <StyledNav aria-label={t("navigation.categoryNavigation")}>
      {allNodes.map((node) => (
        <NavigationLevel
          key={node.id}
          node={node}
          visibleNodes={visibleNodes}
          onItemClick={onItemClick}
          onItemHover={onItemHover}
          onItemLeave={onItemLeave}
        />
      ))}
    </StyledNav>
  );
};

interface NavigationLevelProps {
  node: TreeNode;
  visibleNodes: TreeNode[];
  onItemClick?: (node: TreeNode) => void;
  onItemHover?: (node: TreeNode, noChildren?: boolean) => void;
  onItemLeave?: (node: TreeNode) => void;
}

const NavigationLevel = ({ node, visibleNodes, onItemClick, onItemHover, onItemLeave }: NavigationLevelProps) => {
  const { t } = useTranslation();
  const isVisible = visibleNodes.includes(node);
  const isHoverEnabled = useMediaQuery("(hover: hover)");

  if (node.childNodes.length === 0) {
    return null;
  }

  return (
    <StyledNavigationLevel
      isVisible={isVisible}
      style={{ "--desktop-column": node.level + 1 } as CSSProperties}
      tracking-aux-navigation={"true"}
    >
      {node.level >= 0 && (
        <>
          <NavigationLevelTitle
            href={node.url}
            target={node.openInNewTab ? "_blank" : undefined}
            tracking-aux-navigation-name={`Overview ${t(node.title, {
              lng: "en",
            })}`}
            tracking-aux-navigation-url={node.url}
            tracking-aux-navigation-level={`Level ${node.level + 1}`}
          >
            <span>{t(node.title)}</span>
            <ArrowForward />
          </NavigationLevelTitle>
          <Divider sx={{ marginBlock: 1 }} />
        </>
      )}
      <MenuList>
        {!isHoverEnabled && node.url && (
          <a href={node.url}>
            <StyledNavigationItem>
              <span>{t("notifications.viewAll")}</span>
            </StyledNavigationItem>
          </a>
        )}
        {node.childNodes.map((childNode) => (
          <NavigationTreeItem
            key={childNode.id}
            node={childNode}
            selected={visibleNodes.includes(childNode)}
            onClick={onItemClick}
            onMouseEnter={onItemHover}
            onMouseLeave={onItemLeave}
          />
        ))}
      </MenuList>
    </StyledNavigationLevel>
  );
};

interface NavigationTreeItemProps {
  node: TreeNode;
  selected: boolean;
  onClick?: (node: TreeNode) => void;
  onMouseEnter?: (node: TreeNode, noChildren?: boolean) => void;
  onMouseLeave?: (node: TreeNode) => void;
}

const NavigationTreeItem = ({ node, selected, onClick, onMouseEnter, onMouseLeave }: NavigationTreeItemProps) => {
  const { t } = useTranslation();
  const isHoverEnabled = useMediaQuery("(hover: hover)");
  const isMinDesktop = useIsMinDesktopSize();
  const hasChildren = node.childNodes.length > 0;

  const props =
    (isHoverEnabled && isMinDesktop) || !hasChildren
      ? {
          component: "a",
          href: node.url,
          target: node.openInNewTab ? "_blank" : undefined,
          onMouseEnter: () => onMouseEnter?.(node, !hasChildren),
          onMouseLeave: () => onMouseLeave?.(node),
        }
      : {
          onClick: () => onClick?.(node),
        };

  return (
    <StyledNavigationItem
      {...props}
      selected={selected}
      tracking-aux-navigation-name={t(node.title, { lng: "en" })}
      tracking-aux-navigation-url={node.url}
      tracking-aux-navigation-level={`category level ${node.level}`}
    >
      {node.smallImage && <img className={"icon"} src={node.smallImage.url} alt={node.smallImage.altText ?? ""} />}
      <span>{t(node.title)}</span>
      {hasChildren && <ArrowRight sx={{ marginLeft: "auto" }} />}
    </StyledNavigationItem>
  );
};

const StyledNav = styled("nav")(({ theme }) => ({
  position: "relative",
  display: "grid",
  overflow: "hidden",
  width: "100%",

  [theme.breakpoints.down("desktop-s")]: {
    "& > *": {
      gridRow: "1",
      gridColumn: "1",
    },
  },

  [theme.breakpoints.up("desktop-s")]: {
    gridAutoColumns: "288px",
    gridAutoFlow: "column",
    gap: theme.spacing(2),
  },
  [theme.breakpoints.up("desktop-m")]: {
    gridAutoColumns: "358px",
  },
}));

const StyledNavigationLevel = styled("div")<{ isVisible: boolean }>(({ theme, isVisible }) => ({
  [theme.breakpoints.down("desktop-s")]: {
    visibility: isVisible ? "visible" : "hidden",
    height: isVisible ? "auto" : 0,
    overflow: isVisible ? "auto" : "hidden",
    pointerEvents: isVisible ? "auto" : "none",
    opacity: isVisible ? 1 : 0,
    position: isVisible ? "relative" : "absolute",
    transition: theme.transitions.create(["visibility", "height", "opacity"], {
      duration: theme.transitions.duration.short,
    }),
  },

  [theme.breakpoints.up("desktop-s")]: {
    display: isVisible ? "block" : "none",
    gridColumn: "var(--desktop-column, 1)",
    gridRow: 1,
  },
}));

const NavigationLevelTitle = styled("a")(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(1, 2),
  textDecoration: "none",
  color: theme.palette.text.primary,
  ...getFontStyle(FontSize.TitleMedium),
  gap: "8px",
  "&:hover": {
    color: theme.palette.primary.main,
    cursor: "pointer",
    textDecoration: "underline",
  },
}));
